import { useState } from 'react'
import {
  ChevronDown,
  ChevronRight,
  Lock,
  Calendar,
  Palette,
  Globe,
  Users,
  FileText,
  MessageSquare,
  Monitor,
  Brain,
  Bot,
  Rocket,
  Target,
  DollarSign,
  BarChart3,
  Megaphone,
  CheckCircle2,
  ExternalLink,
  Clock,
} from 'lucide-react'

const AGENDA_PASSWORD = 'dizee2026'

interface AgendaItem {
  title: string
  description: string
  icon: typeof Calendar
}

interface Session {
  id: string
  number: number
  title: string
  subtitle: string
  time: string
  color: 'sky' | 'violet' | 'emerald' | 'amber' | 'rose' | 'cyan'
  attendees: string
  items: AgendaItem[]
}

interface DaySchedule {
  date: string
  label: string
  theme: string
  sessions: Session[]
}

const colorMap: Record<string, { border: string; bg: string; text: string; pill: string; bannerBg: string; iconBg: string }> = {
  sky: {
    border: 'border-sky-500/20',
    bg: 'bg-sky-500/[0.04]',
    text: 'text-sky-400',
    pill: 'bg-sky-500/15 text-sky-300',
    bannerBg: 'bg-sky-500/[0.06] border-sky-500/15',
    iconBg: 'bg-sky-500/10',
  },
  violet: {
    border: 'border-violet-500/20',
    bg: 'bg-violet-500/[0.04]',
    text: 'text-violet-400',
    pill: 'bg-violet-500/15 text-violet-300',
    bannerBg: 'bg-violet-500/[0.06] border-violet-500/15',
    iconBg: 'bg-violet-500/10',
  },
  emerald: {
    border: 'border-emerald-500/20',
    bg: 'bg-emerald-500/[0.04]',
    text: 'text-emerald-400',
    pill: 'bg-emerald-500/15 text-emerald-300',
    bannerBg: 'bg-emerald-500/[0.06] border-emerald-500/15',
    iconBg: 'bg-emerald-500/10',
  },
  amber: {
    border: 'border-amber-500/20',
    bg: 'bg-amber-500/[0.04]',
    text: 'text-amber-400',
    pill: 'bg-amber-500/15 text-amber-300',
    bannerBg: 'bg-amber-500/[0.06] border-amber-500/15',
    iconBg: 'bg-amber-500/10',
  },
  rose: {
    border: 'border-rose-500/20',
    bg: 'bg-rose-500/[0.04]',
    text: 'text-rose-400',
    pill: 'bg-rose-500/15 text-rose-300',
    bannerBg: 'bg-rose-500/[0.06] border-rose-500/15',
    iconBg: 'bg-rose-500/10',
  },
  cyan: {
    border: 'border-cyan-500/20',
    bg: 'bg-cyan-500/[0.04]',
    text: 'text-cyan-400',
    pill: 'bg-cyan-500/15 text-cyan-300',
    bannerBg: 'bg-cyan-500/[0.06] border-cyan-500/15',
    iconBg: 'bg-cyan-500/10',
  },
}

const schedule: DaySchedule[] = [
  {
    date: 'Wednesday, April 15',
    label: 'Day 1',
    theme: 'Creative, Branding & Alignment',
    sessions: [
      {
        id: 'chris-creative',
        number: 1,
        title: 'Creative & Branding with Chris',
        subtitle: 'Noon – 2:00 PM PST',
        time: '2 hours',
        color: 'violet',
        attendees: 'Eddie, Ryan, Aaron, Chris',
        items: [
          {
            title: 'Social handles review',
            description: 'Finalize which handles Dizee will use across all platforms — Instagram, Twitter/X, TikTok, YouTube, etc.',
            icon: Globe,
          },
          {
            title: 'Video review',
            description: 'Review current video assets, direction, and what needs to be produced for launch.',
            icon: Monitor,
          },
          {
            title: 'Deck review',
            description: 'Walk through existing investor and product decks with Chris for creative and branding feedback.',
            icon: FileText,
          },
          {
            title: 'Public website review',
            description: 'Review current site and discuss direction for the public-facing marketing website.',
            icon: Globe,
          },
          {
            title: 'Website inspiration',
            description: 'Pull references from posh.vip (event platform UX), getvictor.com (platform positioning), and disco.ac (music industry B2B creative).',
            icon: Palette,
          },
          {
            title: 'Product review with Chris',
            description: 'Full product walkthrough so Chris understands the build, user flows, and where creative needs to support the product.',
            icon: Monitor,
          },
          {
            title: 'Community efforts (prereqs)',
            description: 'Surface-level discussion on community strategy to set context for deeper sessions on Thursday and Friday.',
            icon: Megaphone,
          },
        ],
      },
      {
        id: 'founding-sync',
        number: 2,
        title: 'Founding Team Sync',
        subtitle: 'Post-Chris session, ~2:30 PM PST onward',
        time: 'Open-ended',
        color: 'sky',
        attendees: 'Eddie, Ryan, Aaron',
        items: [
          {
            title: 'Document review from Owais',
            description: 'Review all documents and deliverables submitted by Owais. Discuss next steps and any gaps.',
            icon: FileText,
          },
          {
            title: 'Communication flow',
            description: 'Define the initial communication flow between founding managers — which tools, cadence, and channels we use day-to-day.',
            icon: MessageSquare,
          },
          {
            title: 'Founding Managers Program overview',
            description: 'High-level review of the program structure, expectations, and target outcomes.',
            icon: Users,
          },
          {
            title: 'Day 2–3 agenda alignment',
            description: 'Confirm priorities and time allocation for Thursday and Friday based on how Day 1 goes.',
            icon: Calendar,
          },
        ],
      },
    ],
  },
  {
    date: 'Thursday, April 16',
    label: 'Day 2',
    theme: 'Product Deep-Dive & Vision',
    sessions: [
      {
        id: 'product-review',
        number: 3,
        title: 'Product & Vision Review',
        subtitle: 'Morning / Early Afternoon',
        time: 'Half day',
        color: 'emerald',
        attendees: 'Eddie, Ryan, Aaron',
        items: [
          {
            title: 'In-depth product review',
            description: 'Full walkthrough of the Dizee product — every feature, flow, and current state. Hands-on demo.',
            icon: Monitor,
          },
          {
            title: 'Vision deck review',
            description: 'Review the vision deck and strategic positioning. Align on how we talk about Dizee externally.',
            icon: FileText,
          },
          {
            title: 'Simple deck review',
            description: 'Review the slimmed-down deck (/simple-deck) — is this ready for investor and partner conversations?',
            icon: FileText,
          },
          {
            title: 'Backend system walkthrough',
            description: 'Eddie presents the full backend architecture — how the system works end-to-end, services, data flow, integrations, AI layer.',
            icon: Monitor,
          },
        ],
      },
      {
        id: 'internal-products',
        number: 4,
        title: 'Internal Products & Agent Architecture',
        subtitle: 'Afternoon',
        time: 'Half day',
        color: 'cyan',
        attendees: 'Eddie, Ryan, Aaron',
        items: [
          {
            title: 'Product tiers presentation',
            description: 'Walk through the three-tier product model: Internal Products → Client Products → End User Products. How they connect and why this architecture matters.',
            icon: Brain,
          },
          {
            title: 'Internal products brainstorm',
            description: 'Define what internal products we need first — executive dashboards, KPI monitoring, agent workflows, admin tooling.',
            icon: Bot,
          },
          {
            title: 'Agent setup review',
            description: 'Review current internal agent system (Telegram bot, knowledge pack architecture) and the path toward a full internal admin AI.',
            icon: Bot,
          },
          {
            title: 'How each founder leverages agents',
            description: 'Practical session — what does Eddie\'s agent workflow look like vs. Ryan\'s vs. Aaron\'s? How does each person use the internal system?',
            icon: Users,
          },
          {
            title: 'Defining "Dizee is AGI"',
            description: 'How the product should feel like one unified brain. Internal, client, and end-user products all connected through a shared intelligence layer.',
            icon: Brain,
          },
        ],
      },
    ],
  },
  {
    date: 'Friday, April 17',
    label: 'Day 3',
    theme: 'Go-to-Market, Community & Operations',
    sessions: [
      {
        id: 'gtm',
        number: 5,
        title: 'Go-to-Market Strategy',
        subtitle: 'Morning',
        time: 'Half day',
        color: 'amber',
        attendees: 'Eddie, Ryan, Aaron',
        items: [
          {
            title: 'Go-to-market strategy',
            description: 'Overall GTM approach — positioning, channels, timing, and how we differentiate from everything else in the market.',
            icon: Rocket,
          },
          {
            title: 'Go-to-beta plan',
            description: 'What does "beta" look like? Who gets access first? What\'s the rollout sequence? How do we gate and control the experience?',
            icon: Target,
          },
          {
            title: 'Go-to-market readiness markers',
            description: 'Define concrete milestones that signal we\'re ready to launch — X users on Founding Managers Program, product stability benchmarks, content pipeline ready, etc.',
            icon: CheckCircle2,
          },
          {
            title: 'KPIs per founder',
            description: 'What does each person need to hit once we go to market? Individual accountability metrics and targets.',
            icon: BarChart3,
          },
        ],
      },
      {
        id: 'community-ops',
        number: 6,
        title: 'Community, Budget & Operations',
        subtitle: 'Afternoon',
        time: 'Half day',
        color: 'rose',
        attendees: 'Eddie, Ryan, Aaron',
        items: [
          {
            title: 'Community efforts (extended)',
            description: 'Deep session on community building strategy — how we grow, engage, and retain early users. Platforms, tactics, and content.',
            icon: Megaphone,
          },
          {
            title: 'Founding Managers Program (deep dive)',
            description: 'Detailed review of onboarding flow, expectations, incentives, and target numbers. How does this program drive our initial growth?',
            icon: Users,
          },
          {
            title: 'Budget review',
            description: 'All initial expenses for go-to-market: tooling, marketing spend, content production, paid channels, operational costs. What do we need to fund?',
            icon: DollarSign,
          },
          {
            title: 'Agent workflow for GTM',
            description: 'How the internal admin system and agent workflows can specifically support go-to-market efforts and reduce manual work.',
            icon: Bot,
          },
        ],
      },
      {
        id: 'wrap-up',
        number: 7,
        title: 'Wrap-Up & Next Steps',
        subtitle: 'Late Afternoon',
        time: '1–2 hours',
        color: 'sky',
        attendees: 'Eddie, Ryan, Aaron',
        items: [
          {
            title: 'Roles & responsibilities',
            description: 'Confirm who owns what across product, GTM, community, and operations. Clear lines of accountability.',
            icon: Users,
          },
          {
            title: '30/60/90-day milestones',
            description: 'Set concrete targets for the next three months post-trip. What does success look like at each checkpoint?',
            icon: Target,
          },
          {
            title: 'Investor & fundraising next steps',
            description: 'Align on fundraising timeline, who to approach, and deck readiness for conversations.',
            icon: DollarSign,
          },
          {
            title: 'Action items & accountability',
            description: 'Document every commitment, owner, and deadline from the week. No item leaves without an owner.',
            icon: CheckCircle2,
          },
        ],
      },
    ],
  },
]

const additionalTopics = [
  { title: 'Beta onboarding flow', description: 'Walk through the first user experience end-to-end and stress-test it together.' },
  { title: 'Content strategy & creative pipeline', description: 'What recurring content does Dizee produce? Social, email, blog, video cadence.' },
  { title: 'Technical infrastructure review', description: 'Hosting, deployment, CI/CD, monitoring — is the stack ready for real users?' },
  { title: 'Legal & compliance checkpoint', description: 'Entity structure, terms of service, privacy policy status, any blockers.' },
  { title: 'Competitive landscape review', description: 'Quick audit of competitors and how Dizee differentiates right now.' },
  { title: 'Feedback loops', description: 'How do we collect, process, and act on user feedback once beta launches?' },
  { title: 'Partnership pipeline', description: 'Any artist, label, or industry partnerships to activate for launch?' },
]

const inspirationSites = [
  { name: 'Posh', url: 'https://posh.vip', reason: 'Event platform UX, clean design' },
  { name: 'Victor', url: 'https://getvictor.com', reason: 'Platform positioning, landing page' },
  { name: 'Disco', url: 'https://disco.ac', reason: 'Music industry tooling, B2B creative' },
]

export default function AgendaPage() {
  const [authenticated, setAuthenticated] = useState(false)
  const [password, setPassword] = useState('')
  const [error, setError] = useState(false)
  const [openSessions, setOpenSessions] = useState<Set<string>>(new Set())

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault()
    if (password === AGENDA_PASSWORD) {
      setAuthenticated(true)
      setError(false)
    } else {
      setError(true)
    }
  }

  const toggleSession = (id: string) => {
    setOpenSessions((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const expandAll = () => {
    const all = new Set<string>()
    schedule.forEach((day) => day.sessions.forEach((s) => all.add(s.id)))
    setOpenSessions(all)
  }
  const collapseAll = () => setOpenSessions(new Set())

  const totalSessions = schedule.reduce((sum, day) => sum + day.sessions.length, 0)
  const totalItems = schedule.reduce((sum, day) => sum + day.sessions.reduce((s, sess) => s + sess.items.length, 0), 0)

  if (!authenticated) {
    return (
      <div className="fixed inset-0 flex items-center justify-center p-8 bg-zinc-950">
        <div className="w-full max-w-md">
          <div className="mb-10">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
              <span className="text-white text-lg font-semibold tracking-tight">DIZEE</span>
              <span className="px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider bg-white/10 text-zinc-400 rounded">Agenda</span>
            </div>
            <p className="text-[13px] text-zinc-500">Enter the password to view the founders trip agenda.</p>
          </div>
          <form onSubmit={handleLogin} className="space-y-6">
            <div>
              <label className="block text-[12px] text-zinc-400 mb-2 tracking-wide uppercase">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value)
                  setError(false)
                }}
                placeholder="Enter password"
                autoFocus
                className="w-full rounded-lg border border-white/[0.08] bg-white/[0.03] px-4 py-3 text-sm text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-transparent transition-all"
              />
              {error && (
                <p className="mt-2 text-[11px] text-rose-400">Incorrect password. Try again.</p>
              )}
            </div>
            <button
              type="submit"
              className="w-full rounded-lg bg-white px-6 py-3 text-sm font-medium text-zinc-950 hover:bg-zinc-100 focus:outline-none focus:ring-2 focus:ring-white/50 transition-all"
            >
              View Agenda
            </button>
          </form>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <div className="fixed inset-0 pointer-events-none z-0 overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-[0.015] rotate-[-25deg]">
          <span className="text-[180px] font-bold tracking-widest text-white whitespace-nowrap select-none">
            CONFIDENTIAL
          </span>
        </div>
      </div>

      <header className="sticky top-0 z-50 backdrop-blur-xl bg-zinc-950/80 border-b border-white/[0.06]">
        <div className="max-w-5xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-5 h-5 bg-white rounded-sm flex-shrink-0" />
            <span className="text-white text-base font-semibold tracking-tight">DIZEE</span>
            <span className="px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider bg-white/[0.08] text-zinc-300 rounded-full">
              Founders Trip
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-[11px] text-zinc-600 uppercase tracking-wider">Confidential</span>
            <Lock className="w-3.5 h-3.5 text-zinc-600" />
          </div>
        </div>
      </header>

      <main className="relative z-10 max-w-5xl mx-auto px-6 py-10 space-y-12">
        {/* Hero */}
        <div>
          <h1 className="text-2xl font-bold text-white mb-3">Founders Trip Agenda</h1>
          <p className="text-[13px] text-zinc-400 leading-relaxed max-w-3xl">
            In-person founders meeting spanning three days. Wednesday kicks off with a creative and branding session with Chris, followed by founding team alignment. Thursday is a full product deep-dive and vision session. Friday focuses on go-to-market, community, budgeting, and action items.
          </p>
          <div className="flex items-center gap-4 mt-5">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium bg-white/[0.06] text-zinc-300">
              <Calendar className="w-3.5 h-3.5" />
              April 15–17, 2026
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-medium bg-white/[0.06] text-zinc-300">
              <Users className="w-3.5 h-3.5" />
              Eddie, Ryan, Aaron + Chris (Day 1)
            </span>
          </div>
        </div>

        {/* Stats bar */}
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Days', value: '3', sub: 'Wed – Fri' },
            { label: 'Sessions', value: String(totalSessions), sub: 'Across all days' },
            { label: 'Agenda Items', value: String(totalItems), sub: 'Topics to cover' },
          ].map((stat) => (
            <div
              key={stat.label}
              className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5"
            >
              <p className="text-2xl font-bold text-white">{stat.value}</p>
              <p className="text-[12px] text-zinc-400 mt-1">{stat.label}</p>
              <p className="text-[10px] text-zinc-600 mt-0.5">{stat.sub}</p>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="flex items-center gap-3">
          <button
            onClick={expandAll}
            className="px-3 py-1.5 rounded-lg text-[11px] font-medium bg-white/[0.06] text-zinc-400 hover:text-white hover:bg-white/[0.1] transition-all"
          >
            Expand All
          </button>
          <button
            onClick={collapseAll}
            className="px-3 py-1.5 rounded-lg text-[11px] font-medium bg-white/[0.06] text-zinc-400 hover:text-white hover:bg-white/[0.1] transition-all"
          >
            Collapse All
          </button>
        </div>

        {/* Schedule */}
        {schedule.map((day) => (
          <div key={day.date} className="space-y-6">
            {/* Day header */}
            <div className="flex items-center gap-4">
              <span className="px-3 py-1.5 rounded-lg text-[11px] font-bold uppercase tracking-wider bg-white/[0.08] text-white">
                {day.label}
              </span>
              <div>
                <h2 className="text-lg font-semibold text-white">{day.date}</h2>
                <p className="text-[12px] text-zinc-500">{day.theme}</p>
              </div>
            </div>

            {/* Sessions for this day */}
            {day.sessions.map((session) => {
              const c = colorMap[session.color]
              const isOpen = openSessions.has(session.id)

              return (
                <div
                  key={session.id}
                  className={`rounded-xl border ${c.border} ${c.bg} overflow-hidden transition-all`}
                >
                  {/* Session header (clickable) */}
                  <button
                    onClick={() => toggleSession(session.id)}
                    className="w-full text-left px-6 py-5 flex items-center justify-between group"
                  >
                    <div className="flex items-center gap-4">
                      <span className={`w-9 h-9 rounded-lg flex items-center justify-center text-sm font-bold ${c.pill}`}>
                        {session.number}
                      </span>
                      <div>
                        <h3 className="text-[14px] font-semibold text-white">{session.title}</h3>
                        <div className="flex items-center gap-3 mt-1">
                          <span className="flex items-center gap-1 text-[11px] text-zinc-500">
                            <Clock className="w-3 h-3" />
                            {session.subtitle}
                          </span>
                          <span className="text-[10px] text-zinc-600">•</span>
                          <span className="text-[11px] text-zinc-500">{session.attendees}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2.5 py-0.5 rounded-full text-[10px] font-semibold ${c.pill}`}>
                        {session.items.length} items
                      </span>
                      {isOpen ? (
                        <ChevronDown className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300 transition-colors" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-zinc-300 transition-colors" />
                      )}
                    </div>
                  </button>

                  {/* Session items (collapsible) */}
                  {isOpen && (
                    <div className="px-6 pb-6 space-y-3">
                      <div className="border-t border-white/[0.06] pt-4" />
                      {session.items.map((item, idx) => {
                        const Icon = item.icon
                        return (
                          <div
                            key={idx}
                            className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-start gap-4"
                          >
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${c.iconBg}`}>
                              <Icon className={`w-4 h-4 ${c.text}`} />
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-[13px] font-medium text-white">{item.title}</h4>
                              <p className="text-[12px] text-zinc-500 leading-relaxed mt-1">{item.description}</p>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ))}

        {/* Inspiration sites */}
        <div className="pt-4">
          <h2 className="text-lg font-semibold text-white mb-2">Website Inspiration</h2>
          <p className="text-[12px] text-zinc-500 mb-5">
            Reference platforms for the public website discussion with Chris.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {inspirationSites.map((site) => (
              <a
                key={site.name}
                href={site.url}
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-5 hover:bg-white/[0.04] transition-all group"
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[14px] font-semibold text-white">{site.name}</span>
                  <ExternalLink className="w-3.5 h-3.5 text-zinc-600 group-hover:text-zinc-400 transition-colors" />
                </div>
                <p className="text-[11px] text-zinc-500">{site.reason}</p>
                <p className="text-[10px] text-zinc-700 mt-2">{site.url}</p>
              </a>
            ))}
          </div>
        </div>

        {/* Additional topics */}
        <div className="pt-4">
          <h2 className="text-lg font-semibold text-white mb-2">Additional Topics to Consider</h2>
          <p className="text-[12px] text-zinc-500 mb-5">
            Suggestions to weave into sessions or add as standalone discussions if time permits.
          </p>
          <div className="space-y-2.5">
            {additionalTopics.map((topic, i) => (
              <div
                key={i}
                className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 flex items-start gap-3"
              >
                <span className="w-5 h-5 rounded-full bg-white/[0.06] flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-[10px] font-bold text-zinc-500">{i + 1}</span>
                </span>
                <div>
                  <h4 className="text-[13px] font-medium text-zinc-300">{topic.title}</h4>
                  <p className="text-[12px] text-zinc-600 mt-0.5">{topic.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Reference links */}
        <div className="pt-4">
          <h2 className="text-lg font-semibold text-white mb-2">Reference Materials</h2>
          <p className="text-[12px] text-zinc-500 mb-5">
            Internal documents and resources relevant to the agenda.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: 'April Plan', route: '/plan-april', description: 'Full development plan, codebase audit' },
              { label: 'Action Plan', route: '/action-plan', description: 'GTM, product, API, fundraising' },
              { label: 'Simple Deck', route: '/simple-deck', description: 'Slimmed-down investor deck' },
              { label: 'Product Deck', route: '/product-deck', description: 'Product-focused presentation' },
              { label: 'Team Handbook', route: '/team-handbook', description: 'How the system works (plain English)' },
              { label: 'Strategic Advisory Group', route: '/strategic-advisory-group', description: 'Founding Circle program' },
            ].map((ref) => (
              <a
                key={ref.route}
                href={ref.route}
                className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4 hover:bg-white/[0.04] transition-all group flex items-center justify-between"
              >
                <div>
                  <span className="text-[13px] font-medium text-zinc-300 group-hover:text-white transition-colors">{ref.label}</span>
                  <p className="text-[11px] text-zinc-600 mt-0.5">{ref.description}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-zinc-700 group-hover:text-zinc-400 transition-colors flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      </main>

      <footer className="border-t border-white/[0.06] mt-16">
        <div className="max-w-5xl mx-auto px-6 py-6 flex items-center justify-between">
          <span className="text-[11px] text-zinc-600">Confidential — Internal Use Only</span>
          <span className="text-[11px] text-zinc-700">Founders Trip — April 2026</span>
        </div>
      </footer>
    </div>
  )
}
